
import { BlogPost } from "@/types/blog";

export const post: BlogPost = {
  slug: "how-to-use-fordje-across-the-build-lifecycle",
  title: "How to Use Fordje Across the Build Lifecycle",
  excerpt: "A practical guide for integrating Fordje's regulatory data tools at each stage of development—from early site selection through final inspection.",
  content: `
    <p>
      We've had more and more teams tell us they're excited about what Fordje can do, but they aren't always sure <em>how</em> to plug it into their workflow. 
      That's fair. Regulatory data isn't something you typically expect to move fast or slot neatly into software tools.
    </p>
    
    <p>
      This post walks through the major stages of the build lifecycle and explains where Fordje fits in, what it replaces (or complements), 
      and how different roles can use it. If you're trying to move faster and avoid costly missteps, this is a practical guide to making 
      Fordje part of how your team works.
    </p>
    
    <h2>Prospecting New Geographies</h2>
    
    <p>
      The first big decision is <em>where</em> to go. Whether you're a solar developer or a land planner for a housing developer, 
      you're trying to figure out where the market is headed, what regions are underserved, and where your project type is actually allowed.
    </p>
    
    <p>
      Traditionally, teams use a mix of market research, incentives data, competitor movement, and gut instinct. But that only gets you so far. 
      Even after narrowing down the list, you still have to answer the compliance question: <em>Can I even build this here?</em>
    </p>
    
    <p>
      <strong>Where Fordje fits in:</strong>
      With the <strong>Synthesize Tool</strong>, you can compare zoning, permitting, and building rules across multiple cities at once. 
      Filter by the constraints that matter to your product, like setbacks, fire requirements, snow load, and quickly get a shortlist of viable 
      regions or understand where you may need to influence local requirements. Then, dig deeper using the <strong>Research Tool</strong> to 
      uncover specific quirks of each city (like odd facade requirements or definitions of "accessory structures").
    </p>
    
    <p>
      Teams often start this step in platforms like <a href="https://www.acres.com" target="_blank" rel="noopener noreferrer"><strong>Acres</strong></a> or a shared <strong>Excel</strong> planning sheet. 
      Fordje slides in early to give confidence that you're not building your strategy on shaky regulatory ground.
    </p>
    
    <h2>Site Assessment (Early Design & Feasibility)</h2>
    
    <p>
      Now you're looking at a specific site. This stage is all about understanding what the project can actually look like on that parcel you've chosen.
    </p>
    
    <p>
      For clean tech projects, you're checking whether the proposed system aligns with the AHJ rules. Can a ground mount go in the yard? 
      Can a battery be installed in the garage? What are the electrical requirements for an EV charging station?
    </p>
    
    <p>
      For housing, you're preparing the site itself - understanding how many units can go on the land based on setbacks, floor area ratios (FAR), 
      and whether the lot can be subdivided under current zoning, and preparing for re-zoning discussions with the city.
    </p>
    
    <p>
      <strong>Where Fordje fits in:</strong>
      Fordje becomes your quick filter. Pull up the <strong>Synthesize Tool</strong>, select relevant data points like setbacks and FAR limits, 
      and run through potential layouts or feasibility checks. You can plug this data into your layout tools, or simply keep it alongside while 
      testing assumptions.
    </p>
    
    <p>
      Fordje helps you avoid surprises and arms your team with early, accurate info before engaging city staff or engineers.
    </p>
    
    <h2>Design and Engineering</h2>
    
    <p>
      This is where vision turns into blueprints. Your design and engineering teams are now creating full plan sets - technical diagrams, 
      construction documents, and spec sheets. Whether it's a house or a solar array, the goal is the same: get it right the first time.
    </p>
    
    <p>
      The problem? Requirements like facade aesthetics, fenestration designs, or maximum height limits vary wildly across 
      jurisdictions—and pulling the wrong detail can lead to rework or rejections.
    </p>
    
    <p>
      <strong>Where Fordje fits in:</strong>
      Fordje acts as your real-time rulebook. Teams use the <strong>Synthesize Tool</strong> to pull relevant requirements for the city 
      they're designing in, then either integrate those via API or pull them up alongside tools like <a href="https://www.autodesk.com/products/autocad" target="_blank" rel="noopener noreferrer"><strong>AutoCAD</strong></a>, <a href="https://www.autodesk.com/products/revit" target="_blank" rel="noopener noreferrer"><strong>Revit</strong></a>, 
      or <a href="https://www.sketchup.com" target="_blank" rel="noopener noreferrer"><strong>SketchUp</strong></a>.
    </p>
    
    <p>
      Engineers often use Fordje to check specific constraints - like the exact setback requirement - <em>while they work</em>, rather than 
      adjusting the design later when issues are caught. Some teams even build a <strong>pre-inspection checklist</strong> from Fordje to ensure 
      inspections go smoothly on site.
    </p>
    
    <p>
      We're not replacing design tools. We're making sure the assumptions behind them are accurate, and that mistakes get caught before 
      they're baked into the design.
    </p>
    
    <h2>Permitting</h2>
    
    <p>
      Submitting a permit is rarely simple. It's often one of the most frustrating parts of the build lifecycle, especially when small details 
      like file naming or paper size requirements cause delays.
    </p>
    
    <p>
      Some clean tech projects require one permit per install depending on the size and city, while residential developers can face a dozen 
      across grading, mechanical, utility, and roadway. Requirements vary widely, and cities aren't always clear about what's expected.
    </p>
    
    <p>
      <strong>Where Fordje fits in:</strong>
      Fordje clarifies what the city actually wants. Teams use the <strong>Synthesize Tool</strong> to check submittal requirements for each 
      AHJ - everything from accepted paper sizes to naming conventions to required documents and fee schedules. Submit to the city, or with permit software like 
      <a href="https://www.permitflow.com" target="_blank" rel="noopener noreferrer"><strong>PermitFlow</strong></a> or 
      <a href="https://solarapp.nrel.gov" target="_blank" rel="noopener noreferrer"><strong>SolarAPP+</strong></a>, easily.
    </p>
    
    <p>
      We help engineering teams <em>and</em> permitting coordinators get the finicky details right so a misnamed file or missing form 
      doesn't stall your project.
    </p>
    
    <h2>On-Site & Inspections</h2>
    
    <p>
      And finally you get to build! Teams are on-site bringing projects to life. Throughout the build process, inspectors come in to check trade work, 
      code compliance, and final approvals. For solar, there might be one inspection; for housing, it could be a dozen.
    </p>
    
    <p>
      When something changes on-site - or when an inspector flags an issue - you need to be able to respond quickly and confidently.
    </p>
    
    <p>
      <strong>Where Fordje fits in:</strong>
      Fordje is your on-site reference. Use the <strong>Research Tool</strong> to clarify AHJ requirements when changes come up or when 
      you need to be aligned with an inspector. That way, both parties are literally looking at the same code.
    </p>
    
    <p>
      In many cases, a <strong>pre-inspection checklist</strong>—created earlier using the <strong>Synthesize Tool</strong>—helps the field team know 
      what to expect before anyone shows up.
    </p>
    
    <h2>Pro Tip:</h2>
    
    <p>
      Build a quick Fordje summary report per AHJ and link it to each project in <a href="https://www.procore.com" target="_blank" rel="noopener noreferrer"><strong>Procore</strong></a> or your CRM of choice. Makes it easier for field, 
      permitting, and design teams to stay aligned.
    </p>
    
    <div className="bg-fordje-blue/10 p-6 rounded-lg my-8">
      <h3 class="text-xl font-bold mb-2">Ready to dive deeper?</h3>
      <p>
        This is just the start. We will deep dive into each of these stages, complete with tool integrations and real examples.
      </p>
      <p class="mt-4">
        In the meantime, if you're trying to figure out <em>how</em> to fit Fordje into your team's workflow, drop us a line. 
        We're happy to show you how to save money and speed up your projects.
      </p>
    </div>
  `,
  coverImage: "/lovable-uploads/871a7d07-57d1-4f0e-bc09-b39e2e4e55c0.png",
  date: "2025-05-22T09:00:00",
  readTime: 8,
  categories: ["How To", "Clean Tech", "Construction"],
  author: {
    name: "Gillian Wildfire",
    title: "Co-founder & CEO",
    avatar: "/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png"
  }
};

